import { Button } from '@appkit4/react-components';
import {
    DeclarationTypes,
    NewDeclarationFormValues,
} from 'pages/SubmissionFormWizard/SubmissionFormWizard';
import { FormikProps, useFormikContext } from 'formik';
import { useCallback } from 'react';
import useLocale from 'locale/useLocale';
import hasErrors from 'helpers/hasErrors';
import { useUserDeclarations } from 'contexts/UserDeclarationsProvider/UserDeclarationsProvider';

export type NewDeclarationFormFooterProps = {
    step: number;
    stepsLength: number;
    handleLeftButtonClick: () => void;
    handleRightButtonClick: (
        formikProps: FormikProps<NewDeclarationFormValues>
    ) => Promise<void>;
    submissionIsPending: boolean;
    submissionIdForEditFlow?: string;
};

const SubmissionWizardFooter = ({
    step,
    stepsLength,
    handleLeftButtonClick,
    handleRightButtonClick,
    submissionIsPending,
    submissionIdForEditFlow,
}: NewDeclarationFormFooterProps) => {
    const { l } = useLocale();
    const { values, errors } = useFormikContext<NewDeclarationFormValues>();
    const formikProps = useFormikContext<NewDeclarationFormValues>();

    const renderRightButtonText = useCallback(() => {
        if (step === stepsLength - 2) {
            return 'Submit';
        }
        return 'Next';
    }, [step, stepsLength]);

    const { error: userDeclarationsError } = useUserDeclarations();

    const rightButtonDisabled = useCallback(() => {
        switch (step) {
            case 0:
                return !values.declarationType;
            case 1:
                if (values.declarationType === DeclarationTypes.Planned) {
                    return !values.reasonForAbsence;
                }
                if (values.declarationType === DeclarationTypes.Unforeseen) {
                    return (
                        !values.unforeseenAbsences ||
                        values.unforeseenAbsences.some(
                            (absence) =>
                                !absence.startDate ||
                                !absence.reasonForAbsence ||
                                absence.reasonForAbsence.trim() === ''
                        ) ||
                        !formikProps.dirty ||
                        userDeclarationsError
                    );
                }
                break;
            case 2:
                if (values.declarationType === DeclarationTypes.Planned) {
                    return (
                        !values.dateRanges ||
                        values.dateRanges.some(
                            (range) => !range.startDate || !range.endDate
                        ) ||
                        !formikProps.dirty ||
                        hasErrors(errors) ||
                        userDeclarationsError
                    );
                }
                break;
            default:
                return null;
        }
    }, [values, step, errors]);

    return (
        <>
            {step < stepsLength - 1 ? (
                <div className="mt-4 d-flex justify-content-between">
                    {step > 0 &&
                    step < stepsLength - 1 &&
                    (!submissionIdForEditFlow || step === 2) ? (
                        <Button
                            kind="text"
                            icon="icon-arrow-left-small-outline"
                            onClick={handleLeftButtonClick}
                        >
                            {l('_back')}
                        </Button>
                    ) : (
                        <div />
                    )}
                    {step < stepsLength - 1 && (
                        <Button
                            type="button"
                            kind="primary"
                            onClick={() => handleRightButtonClick(formikProps)}
                            disabled={rightButtonDisabled()}
                            loading={submissionIsPending}
                        >
                            {renderRightButtonText()}
                        </Button>
                    )}
                </div>
            ) : null}
        </>
    );
};

export default SubmissionWizardFooter;
